import { createCn } from 'shared-utils'
import type {
  MapLocatorClusteringSettings,
  MapLocatorOriginMarkerSettings,
  MapLocatorLabels,
  MapLocatorMapSettings,
  MapLocatorMarkerSettings,
  MapLocatorPopoverSettings,
  MapLocatorSearchSettings
} from 'ecosystem'

export const mapLocatorCn = createCn('map-locator')

export const MAP_LOCATOR_CN = {
  SECTION: 'section',
  SECTION_CONTAINER: 'section_container',
  WRAPPER: 'wrapper',
  TITLE: 'title',
  DESC: 'description',
  TOOLBAR: 'toolbar',
  SEARCH: 'search',
  FILTERS: 'filters',
  FILTER_CHIP: 'filter_chip',
  BODY: 'body',
  MAP: 'map',
  LIST: 'list',
  LIST_ITEM: 'list_item',
  POPOVER: 'popover',
  PANEL: 'panel',
  CLUSTER: 'cluster',
  MARKER: 'marker',
  ORIGIN: 'origin'
}

/**
 * Google's public development Map ID. Advanced Markers refuse to render without a Map ID,
 * so this keeps the widget usable before a store has provisioned its own in Cloud Console.
 * Production stores should always set `map.mapId`.
 */
export const DEMO_MAP_ID = 'DEMO_MAP_ID'

/**
 * Every APIProvider in the app must pass this exact list. The Maps JS loader is a
 * global singleton: a second provider asking for a different set of libraries is
 * ignored with an "already loaded with different parameters" warning.
 */
export const MAPS_LIBRARIES: ['marker', 'places', 'geocoding'] = ['marker', 'places', 'geocoding']

export const DEFAULT_MAP_SETTINGS: MapLocatorMapSettings = {
  // Stockholm — matches the default of the existing single-point map widget
  defaultCenter: { lat: 59.3254067, lng: 18.0706993 },
  defaultZoom: 5,
  gestureHandling: 'cooperative',
  fitBoundsToItems: true,
  controls: {
    zoom: true,
    mapType: false,
    fullscreen: true,
    streetView: false
  }
}

export const DEFAULT_CLUSTERING_SETTINGS: MapLocatorClusteringSettings = {
  enabled: true,
  maxZoom: 14,
  radius: 80,
  shape: 'PIN',
  borderWidth: 2,
  scaleWithCount: true
}

export const DEFAULT_MARKER_SETTINGS: MapLocatorMarkerSettings = {
  useTagColors: true,
  shape: 'PIN',
  borderWidth: 2
}

export const DEFAULT_POPOVER_SETTINGS: MapLocatorPopoverSettings = {
  mode: 'INFO_WINDOW',
  fields: {
    image: true,
    address: true,
    phone: true,
    email: true,
    website: true,
    tags: true,
    content: true
  }
}

/** A distinct blue so the visitor's own position never reads as a retailer pin. */
export const DEFAULT_ORIGIN_MARKER_SETTINGS: MapLocatorOriginMarkerSettings = {
  color: '#1A73E8',
  borderColor: '#FFFFFF',
  borderWidth: 3,
  size: 22
}

export const DEFAULT_SEARCH_SETTINGS: MapLocatorSearchSettings = {
  enabled: true,
  autocomplete: true,
  showFindMe: true,
  showList: true,
  showDistance: true
}

export const DEFAULT_LABELS: Required<MapLocatorLabels> = {
  searchPlaceholder: 'Search for a city or address',
  findMe: 'Find me',
  findMeLoading: 'Locating…',
  geolocationDenied: 'Location access was denied. Search for a place instead.',
  geolocationUnavailable: 'Your position could not be determined.',
  clearFilters: 'Clear',
  allTags: 'All',
  resultsCount: '{count} results',
  noResults: 'No locations match the current filters.',
  distanceUnit: 'km',
  closePopover: 'Close',
  showOnMap: 'Show on map'
}

export const DEFAULT_HEIGHT = { base: '420px', md: '620px' }

export const DEFAULT_MARKER_COLOR = '#2B6CB0'

export const DEFAULT_CLUSTER_COLOR = '#1A202C'
export const DEFAULT_CLUSTER_TEXT_COLOR = '#FFFFFF'
export const DEFAULT_MARKER_BORDER_COLOR = '#FFFFFF'

/** Matches DEFAULT_CLUSTERING_SETTINGS.maxZoom; above it points are never grouped. */
export const DEFAULT_CLUSTER_MAX_ZOOM = 14

/** Above this many points markers are limited to the current viewport. */
export const VIEWPORT_CULLING_THRESHOLD = 500
